import { useState } from "react";
import { useQuery } from "react-query";
import { HStack, VStack, Flex, Text, Spinner, SkeletonText, useTheme } from "@chakra-ui/react";
import { Check, WarningDiamond } from "@phosphor-icons/react";
import { taskService, isTaskClosed } from "api/ai/ai.service";

/**
 * Show the report planner's section outline as a checklist during report generation, with a
 * tick/spinner/warning per section so the user can see real progress (which sections are
 * planned, which are done, which failed) instead of only the abstract percentage from
 * AIProgressWidget.
 *
 * Polls the same task the same way as AIProgressWidget (same taskService.get + query key), so
 * React Query dedupes the two components' polling into one network request per interval.
 *
 * Reads task.outputs.outline ([{section_id, title, action, depends_on}], set by
 * esgai/tasks/runner.py::run_report_writer_task() right after plan_report() returns) and
 * task.outputs.section_results ({section_id: {success, error, error_detail, ...}}, populated
 * incrementally as each section worker finishes).
 *
 * A section with `depends_on` runs in a later execution tier (ESG-3953), so it stays idle until
 * its dependencies finish. Such a row shows "waiting for X, Y" with the static pending dot rather
 * than a spinner, so queued-behind-a-dependency is visibly different from being-written.
 *
 * `result.error` is always a short, plain-language reason (ESG-3953: backend/checkers.py's
 * user_reason map) safe to show every user - this component renders it ungated during
 * generation. `result.error_detail` is the raw developer-facing text (check name + exception
 * message, can contain UUIDs/collection names) and is only ever shown when `showErrorDetail` is
 * explicitly passed true - callers MUST gate that (e.g. with <EsgAdminOnly>) before setting it.
 */
export default function ReportSectionChecklist({ taskId, refetchInterval = 5000, showErrorDetail = false }) {
  const { colors } = useTheme();
  const [freshTask, setFreshTask] = useState(null);

  useQuery({
    queryKey: ["ai-task", taskId],
    queryFn: () => taskService.get(taskId),
    onSuccess: (data) => setFreshTask(data),
    refetchInterval,
    enabled: taskId && (!freshTask || (refetchInterval > 0 && !isTaskClosed(freshTask))),
  });

  if (!taskId) {
    return null;
  }

  const outline = freshTask?.outputs?.outline;
  const sectionResults = freshTask?.outputs?.section_results || {};

  // Planning phase: the outline hasn't been produced yet (plan_report() is itself an LLM call
  // and can take a while) - show a loading placeholder rather than an empty box.
  if (!outline || outline.length === 0) {
    return (
      <VStack align="stretch" spacing={1} width="100%">
        <Text fontSize="body.xs" color="gray.500">
          Planning sections...
        </Text>
        <SkeletonText mt="1" noOfLines={4} spacing="3" skeletonHeight="2" />
      </VStack>
    );
  }

  // Only spin the pending rows while the task is genuinely being worked on - a queued-but-not-
  // yet-started task shows a static pending dot instead of a busy-spinning list.
  const isActivelyRunning = freshTask?.status === "running";

  // ESG-3953: result.error is always the short, plain-language reason (safe for every viewer).
  // result.error_detail is the raw developer-facing text (check name + exception message) and
  // is only appended when the caller explicitly opted in via showErrorDetail - callers must
  // gate that prop behind an admin check (e.g. <EsgAdminOnly>) before passing it true.
  const failureTooltip = (result) => {
    if (!result?.error) {
      return "Section failed";
    }
    const text = showErrorDetail && result.error_detail
      ? `${result.error} (${result.error_detail})`
      : result.error;
    return String(text).slice(0, 400);
  };

  // ESG-3953: a section that declared depends_on runs in a LATER TIER - it sits idle until every
  // section it depends on has finished. Without this it rendered as a spinning row identical to
  // one actually being written, which on a large report means minutes of a spinner that really
  // means "queued". Resolve which dependencies are still outstanding so the row can say so.
  // A dependency counts as settled once it has a result (success OR failure - a derived section
  // still runs when an upstream failed, it's just told about the gap) or is an unchanged "keep".
  const titleById = {};
  outline.forEach((s) => {
    titleById[s.section_id] = s.title || s.section_id;
  });
  const isSettled = (depId) => {
    const dep = sectionResults[depId];
    if (dep) {
      return true;
    }
    const depOutline = outline.find((s) => s.section_id === depId);
    return depOutline?.action === "keep";
  };
  const pendingDependencyTitles = (section) =>
    (section.depends_on || []).filter((depId) => !isSettled(depId)).map((depId) => titleById[depId] || depId);

  return (
    <VStack align="stretch" spacing={2} width="100%">
      {outline.map((section) => {
        const result = sectionResults[section.section_id];
        const isKept = section.action === "keep";
        const isDone = isKept || result?.success;
        const isFailed = !isKept && result && !result.success;
        const waitingFor = isDone || isFailed ? [] : pendingDependencyTitles(section);
        const isWaiting = waitingFor.length > 0;

        return (
          <HStack key={section.section_id} spacing={2} align="flex-start">
            {isDone && (
              <Flex
                align="center"
                justify="center"
                minW="16px"
                h="16px"
                mt="2px"
                borderRadius="full"
                bg="lightLime"
                color="limeShade.700"
                flexShrink={0}
                aria-label="Section complete"
              >
                <Check size={10} weight="bold" />
              </Flex>
            )}
            {isFailed && (
              <WarningDiamond
                size={16}
                color={colors.red[400]}
                style={{ marginTop: "2px", flexShrink: 0 }}
                title={failureTooltip(result)}
              />
            )}
            {!isDone && !isFailed && (
              <Flex minW="16px" h="16px" mt="2px" align="center" justify="center" flexShrink={0}>
                {/* A waiting section is NOT being worked on - show the static pending dot, not a
                spinner, so "queued behind a dependency" reads differently from "in progress". */}
                {isActivelyRunning && !isWaiting ? (
                  <Spinner size="xs" color="gray.400" />
                ) : (
                  <Flex minW="8px" h="8px" borderRadius="full" bg="gray.300" />
                )}
              </Flex>
            )}
            <Text
              fontSize="body.s"
              color={isFailed ? "red.500" : "gray.700"}
              title={isFailed ? failureTooltip(result) : undefined}
            >
              {section.title}
              {isKept && (
                <Text as="span" color="gray.400">
                  {" "}
                  (unchanged)
                </Text>
              )}
              {isWaiting && (
                <Text as="span" color="gray.400">
                  {" "}
                  — waiting for {waitingFor.join(", ")}
                </Text>
              )}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
}
